//#region Imports
import {
  FormControl,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  Typography
} from '@mui/material';
import { GetApp, Info } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import KogModalSideComponent from '@/main/components/modal/ModalSide';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KOGFileUploader from '@main/components/shared/fileUploader/fileUploader';
import useAsyncModalAlert from '@/main/components/shared/asyncModalAlert/asyncModalAlert';

import {
  supplierApi,
  supplierInventoryImportApi,
  warehouseApi
} from '@/main/api';
import CompanyAllDropdown from '@/main/components/shared/companyAllDropdown/companyAllDropdown';
import { KeyValue } from '@/main/models/common';
import {
  SupplierFilterRequest,
  SupplierInventoryImportUploadRequest,
  WarehouseFilterRequest
} from '@/main/models/request';
import { SupplierInventoryImportDataResponse } from '@/main/models/response';
import { Controls } from '@/main/services/constants/constantsControl';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { ParameterTypeEnum } from '@/main/services/enum/ParameterTypeEnum';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { StatusSupplierInventoryImportEnum } from '@/main/services/enum/StatusSupplierInventoryImportEnum';
import { TemplateImportTypeEnum } from '@/main/services/enum/TemplateImportTypeEnum';
import {
  filterCompanyTypeByEnums,
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  isShowControlByElementId
} from '@/main/services/utils';
import { initalDateForm } from '@/main/services/utilsDate';
import { userIsAdmin, userIsSupplier } from '@/main/services/utilsUser';
import { getCompanyAll } from '@/main/store/company';
import { getCompanyType, useCompanyTypeReducer } from '@/main/store/companyType';
import { useCountryReducer } from '@/main/store/country';
import { useParameterByTypeScreenReducer } from '@/main/store/parameterByTypeScreen';
import { useSupplierReducer } from '@/main/store/supplier';
import { resetReducerWarehouse } from '@/main/store/warehouse';

import { IInitialDataDomain, INITIAL_DATA_DOMAIN } from './utilsModalResultImport';
import { useTabsInventoryContext } from '../context';
import * as S from './styles';
// #endregion

export interface ISearchImportHistoryForm {
  country: KeyValue | null;
  supplier: KeyValue | null;
  warehouse: KeyValue | null;
  companyType: KeyValue | null;
  company: KeyValue | null;
  allCompany: boolean;
}


interface IButtonSubmitProps {
  uploading: boolean;
  files: File[];
}
const ButtonSubmit = ({
  files,
  uploading
}: IButtonSubmitProps) => {
  const { watch } = useFormContext<ISearchImportHistoryForm>();
  const formsWatch = watch();

  const validFields = [
    isNullUndefined(formsWatch.country?.key),
    isNullUndefined(formsWatch.supplier?.key),
    isNullUndefined(formsWatch.warehouse?.key),
    isNullUndefined(formsWatch.companyType?.key),
    isNullUndefined(formsWatch.company?.key) && !formsWatch.allCompany
  ];

  const isDisabled = uploading || files.length === 0 || validFields.some(field => field);

  return (
    <SecondaryButton
      type="submit"
      id="id-modalImportInventory-import-button"
      disabled={isDisabled}>
      {uploading
        ? getTranslateByKey('importInventory.uploading') + '...'
        : getTranslateByKey('importInventory.import')}
    </SecondaryButton>
  )
}
interface Props {
  open?: boolean;
  closeModal: () => void;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  onSearchGrid: any;
  setTabCurrent: (tab: number) => void;
  exporting: boolean;
  setExporting: (value: boolean) => void;
  uploading: boolean;
  setUploading: (value: boolean) => void;
}

const ModalImportInvetory: React.FC<Props> = ({
  open,
  closeModal,
  files,
  setFiles,
  onSearchGrid,
  setTabCurrent,
  exporting,
  setExporting,
  uploading,
  setUploading
}: Props) => {

  // #region custom hooks
  const {
    setDataReturnImport,
    setOpenModalReturnImport
  } = useTabsInventoryContext();
  const parameterRangeDays = useParameterByTypeScreenReducer(ParameterTypeEnum.DefaultSearchImportsInterval);
  const parameterMaximumImportLines = useParameterByTypeScreenReducer(ParameterTypeEnum.MaximumImportLines);
  // #endregion

  // #region Domains/Reducers
  const dispatch = useDispatch();
  const countryReducer = useCountryReducer();
  const supplierReducer = useSupplierReducer();
  const { companyType: companyTypeReducer } = useCompanyTypeReducer();
  const [openModalAlert, ModalAlert] = useAsyncModalAlert();
  const showCompanyControl = isShowControlByElementId(
    Controls.IdImportSearchInventory
  );

  const [supplierData, setSupplierData] = useState<IInitialDataDomain>(INITIAL_DATA_DOMAIN);
  const [warehouseData, setWarehouseData] = useState<IInitialDataDomain>(INITIAL_DATA_DOMAIN);

  const optionDefaultCountryDrop = getDefaultOptionDrop(countryReducer.countriesUser.data);
  const optionDefaultSupplierDrop = getDefaultOptionDrop(supplierData.data);
  const optionDefaultWarehouseDrop = getDefaultOptionDrop(warehouseData.data);

  const INITAL_STATE_FORM: ISearchImportHistoryForm = {
    country: optionDefaultCountryDrop as KeyValue | null,
    supplier: optionDefaultSupplierDrop as KeyValue | null,
    warehouse: optionDefaultWarehouseDrop as KeyValue | null,
    companyType: null as KeyValue | null,
    company: null as KeyValue | null,
    allCompany: !showCompanyControl as boolean
  }
  const methods = useForm<ISearchImportHistoryForm>({
    mode: 'onChange',
    defaultValues: INITAL_STATE_FORM
  });
  const {
    control,
    setValue,
    getValues,
    formState: { errors }
  } = methods;
  // #endregion

  // #region States
  const isAdmin = userIsAdmin();
  const isSupplier = userIsSupplier();
  const [version, setVersion] = useState(TemplateImportTypeEnum.FullVersion);
  const [template, setTemplate] = useState(false);
  // #endregion

  async function callSupplierApiDomain(optionSelected: KeyValue | null) {
    if (optionSelected !== null) {
      setWarehouseData({ data: null, isLoading: true });
      setSupplierData({ data: null, isLoading: true });
      setValue('supplier', null);
      setValue('warehouse', null);

      const request: SupplierFilterRequest = {
        guidCountry: String(optionSelected.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };
      try {
        const suppliersResponse = await supplierApi.getSuppliersOrSupplierBranchesDomain(
          request
        );
        setSupplierData({
          data: suppliersResponse.data.result,
          isLoading: false
        });
        setWarehouseData({ data: null, isLoading: false });
      } catch (error) {
        setSupplierData({ data: null, isLoading: false });
        setWarehouseData({ data: null, isLoading: false });
      }
    }
  }

  function callWarehouseApiDomain(optionSelected: KeyValue | null) {
    if (optionSelected !== null) {
      setValue('warehouse', null);
      setWarehouseData({ data: null, isLoading: true });
      const request: WarehouseFilterRequest = {
        guidSupplier: String(optionSelected.key)
      };
      warehouseApi
        .getDomain(request)
        .then(response => {
          setWarehouseData({
            data: response.data.result,
            isLoading: false
          });
          if (getDefaultOptionDrop(response.data.result)) {
            setValue('warehouse', response.data.result[0]);
          }
        })
        .catch(() => {
          setWarehouseData({
            data: null,
            isLoading: false
          });
        });
    }
  }

  async function importInventoryAPI() {
    try {
      setUploading(true);
      const formParams = getApiParams();

      if (isNullUndefined(formParams.country?.key) && isAdmin) {
        toast.error(getTranslateByKey('information.countryRequired', true));
        setUploading(false);
        return;
      }

      if (isNullUndefined(formParams.supplier?.key) && isAdmin) {
        toast.error(getTranslateByKey('information.supplierRequired', true));
        setUploading(false);
        return;
      }

      if (isNullUndefined(formParams.warehouse?.key)) {
        toast.error(getTranslateByKey('warehouse.warehouseRequired', true));
        setUploading(false);
        return;
      }
      if (isNullUndefined(formParams.companyType?.key)) {
        toast.error(getTranslateByKey('information.companyTypeRequired', true));
        setUploading(false);
        return;
      }
      if (isNullUndefined(formParams.company?.key) && formParams.allCompany === false) {
        toast.error(getTranslateByKey('company.companyNotInformed', true));
        setUploading(false);
        return;
      }

      const request: SupplierInventoryImportUploadRequest = {
        fullImport: template,
        guidCountry: isSupplier
          ? countryReducer.countriesUser.data[0].key
          : formParams.country?.key,
        guidSupplier: isSupplier
          ? supplierReducer.suppliers.data[0].key
          : formParams.supplier?.key,
        guidWarehouse: formParams.warehouse?.key,
        idCompanyTypeBuyer: Number(formParams.companyType?.key),
        guidCompanyBuyer: formParams.allCompany ? '' : String(formParams.company?.key),
      };

      const file = files[0]; // Get the selected file
      supplierInventoryImportApi
        .uploadImportInventory(request, file)
        .then(response => {
          if (
            !isNullUndefined(response.data) &&
            !isNullUndefined(response.data.result)
          ) {
            const resultData: SupplierInventoryImportDataResponse =
              response.data.result;
            const statusImport = response.data.result.status;
            if (statusImport == StatusSupplierInventoryImportEnum.Success) {
              toast.success(
                getTranslateByKey('importInventory.importCompletedSuccessfully')
              );
            } else if (
              statusImport == StatusSupplierInventoryImportEnum.Partial
            ) {
              toast.warning(
                getTranslateByKey('importInventory.importCompletedPartial')
              );
            } else if (
              statusImport == StatusSupplierInventoryImportEnum.Error
            ) {
              toast.error(
                getTranslateByKey('importInventory.importCompletedError')
              );
            } else if (
              statusImport == StatusSupplierInventoryImportEnum.Scheduled
            ) {
              toast.success(
                getTranslateByKey('importInventory.importCompletedScheduled')
              );
            }
            if (
              version === TemplateImportTypeEnum.SimpleVersion &&
              statusImport !== StatusSupplierInventoryImportEnum.Success
            ) {
              if (!isNullUndefined(resultData)) {
                setDataReturnImport(resultData);
                setOpenModalReturnImport(true);
              }
            }
          }

          // Reload fields & reducers
          if (optionDefaultCountryDrop) {
            callSupplierApiDomain(optionDefaultCountryDrop);
          }
          setValue('supplier', null);
          setValue('warehouse', null);
          dispatch(resetReducerWarehouse());
          setFiles([]);
          closeModal();
          setTabCurrent(0);
          const params = getApiParams();

          onSearchGrid({
            ...params,
            company: formParams.allCompany ? [] : [params.company],
            startDate: initalDateForm(parameterRangeDays),
            finalDate: initalDateForm()
          });
        })
        .finally(() => {
          setUploading(false);
        });
    } catch (error) { }
  }

  function validateImport() {
    if (files.length > 0) {
      importInventoryAPI();
    } else {
      toast.error(
        getTranslateByKey('importInventory.pleaseSelectAtLeast1File')
      );
    }
  }

  function getApiParams() {
    const params: ISearchImportHistoryForm = {
      country: getValues('country'),
      supplier: getValues('supplier'),
      warehouse: getValues('warehouse'),
      companyType: getValues('companyType'),
      company: getValues('company'),
      allCompany: getValues('allCompany')
    };

    return params;
  }

  async function exportSupplierInventories() {
    setExporting(true);

    const formParams = getApiParams();

    if (isNullUndefined(formParams.country?.key) && isAdmin) {
      toast.error(getTranslateByKey('information.countryRequired', true));
      setExporting(false);
      return;
    }

    if (isNullUndefined(formParams.supplier?.key) && isAdmin) {
      toast.error(getTranslateByKey('information.supplierRequired', true));
      setExporting(false);
      return;
    }

    if (isNullUndefined(formParams.warehouse?.key)) {
      toast.error(getTranslateByKey('warehouse.warehouseRequired', true));
      setExporting(false);
      return;
    }

    if (isNullUndefined(formParams.companyType?.key)) {
      toast.error(getTranslateByKey('information.companyTypeRequired', true));
      setExporting(false);
      return;
    }
    if (isNullUndefined(formParams.company?.key) && formParams.allCompany === false) {
      toast.error(getTranslateByKey('company.companyNotInformed', true));
      setExporting(false);
      return;
    }


    supplierInventoryImportApi
      .exportInventory({
        fullImport: template,
        guidSupplier: formParams.supplier?.key.toString(),
        guidWarehouse: formParams.warehouse?.key.toString(),
        idCompanyTypeBuyer: formParams.companyType?.key as number,
        guidCompanyBuyer: formParams.allCompany ? '' : String(formParams.company?.key)
      })
      .then(async (response) => {
        const params = getApiParams();

        setExporting(false);

        const isConfirm = await openModalAlert(getTranslateByKey("information.exportSucceess"));

        if (isConfirm) {
          onSearchGrid({
            ...params,
            company: formParams.allCompany ? [] : [params.company],
            startDate: initalDateForm(parameterRangeDays),
            finalDate: initalDateForm()
          });
        }
      })
      .catch(error => {
        console.error('Error:', error);
        setExporting(false);
      });
  }
  function onChangeCompanyType(companyTypeSelected: KeyValue | null) {
    methods.resetField('company');

    if (isNullUndefined(companyTypeSelected) || isNullUndefined(optionDefaultCountryDrop)) return;

    dispatch(
      getCompanyAll({
        guidCountry: String(optionDefaultCountryDrop.key),
        idCompanyType: String(companyTypeSelected.key),
        idStatusRegister: StatusRegisterEnum.Active
      })
    );
  }

  useEffect(() => {
    if (isNullUndefined(companyTypeReducer.data)) dispatch(getCompanyType());
    if (!isShowControlByElementId(Controls.IdImportTemplate)) {
      setVersion(TemplateImportTypeEnum.SimpleVersion);
      setTemplate(false);
    }
  }, []);

  useEffect(() => {
    if (version === TemplateImportTypeEnum.FullVersion) {
      setTemplate(true);
    } else {
      setTemplate(false);
    }
  }, [version]);

  useEffect(() => {
    // Call API's related by country default
    if (optionDefaultCountryDrop) {
      callSupplierApiDomain(optionDefaultCountryDrop);
    }
  }, [optionDefaultCountryDrop]);

  useEffect(() => {
    if (optionDefaultSupplierDrop) {
      callWarehouseApiDomain(optionDefaultSupplierDrop);
    }
  }, [optionDefaultSupplierDrop]);

  useEffect(() => {
    if (optionDefaultSupplierDrop && isSupplier) {
      callWarehouseApiDomain(optionDefaultSupplierDrop);
      setValue('supplier', optionDefaultSupplierDrop);
    }
  }, [optionDefaultSupplierDrop, isSupplier]);

  const isFullVersion = version === TemplateImportTypeEnum.FullVersion;
  const CompanyTypesFilters = [
    CompanyTypeEnum.BodyShop,
    CompanyTypeEnum.Insurer,
  ]
  const optionsFilteredCompanyType = filterCompanyTypeByEnums(companyTypeReducer.data, CompanyTypesFilters);

  return (
    <>
      {open && (
        <KogModalSideComponent
          open={open}
          closeModal={() => closeModal()}
          id="id-importinventory-modal-scroll">
          <S.Title>{getTranslateByKey('importInventory.import')}</S.Title>
          <S.Background>
            <FormProvider {...methods}>
              {isShowControlByElementId(Controls.IdImportTemplate) && (
                <FormControl
                  component="fieldset"
                  style={{ display: 'flex', alignItems: 'center' }}>
                  <S.CustomRadioGroup
                    value={version}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setVersion(Number(e.currentTarget.value));
                    }}>
                    <FormControlLabel
                      value={TemplateImportTypeEnum.FullVersion}
                      control={
                        <Radio
                          color="primary"
                          id="id-importinventory-modal-radio-fullVersion"
                        />
                      }
                      label={getTranslateByKey('information.fullversion')}
                    />
                    <FormControlLabel
                      value={TemplateImportTypeEnum.SimpleVersion}
                      control={
                        <Radio
                          color="primary"
                          id="id-importinventory-modal-radio-simpleVersion"
                        />
                      }
                      label={getTranslateByKey('information.simpleversion')}
                    />
                  </S.CustomRadioGroup>
                </FormControl>
              )}
              <Typography color="error">
                {isFullVersion
                  ? getTranslateByKey('importInventory.fullVersionWarning')
                  : getTranslateByKey('importInventory.shortVersionWarning')}
              </Typography>
              {isShowControlByElementId(Controls.IdDownloadSupplierInventory) && (
                <>
                  {exporting ? (
                    <S.ProgressContainer>
                      <LinearProgress
                        style={{ flexGrow: 1, marginRight: '8px' }}
                      />
                      <Typography variant="body1">
                        {getTranslateByKey('importInventory.exporting')}...
                      </Typography>
                    </S.ProgressContainer>
                  ) : (
                    <S.DownloadContainer>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          cursor: 'pointer',
                          margin: '0 auto'
                        }}
                        onClick={exportSupplierInventories}>
                        <IconButton
                          size="medium"
                          edge="start"
                          color="primary"
                          aria-label="Download Inventory"
                          style={{ width: 40, top: 4 }}
                          id="id-modalImportInventory-export-button">
                          <GetApp />
                        </IconButton>
                        <Typography
                          variant="body1"
                          style={{
                            fontSize: 14,
                            borderBottom: '1px solid #3f5ec9',
                            marginLeft: '8px',
                            color: '#3f5ec9'
                          }}>
                          {getTranslateByKey('importInventory.clickHere')}
                        </Typography>
                      </div>
                    </S.DownloadContainer>
                  )}
                </>
              )}
              <form
                onSubmit={async e => {
                  e.preventDefault();
                  try {
                    await validateImport();
                  } catch (error) { }
                }}
                noValidate>
                <S.BodyBackground>
                  {isAdmin && (
                    <KogAutoCompleteControlled
                      label={getTranslateByKey('information.country')}
                      options={countryReducer.countriesUser.data}
                      isLoading={countryReducer.countriesUser.isLoading}
                      nameCustom="country"
                      rulesCustom={{ required: true }}
                      errorMessageCustom={
                        errors.country?.type === 'required'
                          ? getTranslateByKey('information.countryRequired', true)
                          : ''
                      }
                      setValueCustom={setValue}
                      disabled={
                        countryReducer.countriesUser.data == null ||
                        optionDefaultCountryDrop != null
                      }
                      id="id-modalImportInventory-txt-country"
                      control={control}
                      onChangeCustom={(optionSelected: KeyValue | null) => {
                        if (optionSelected === null) {
                          setWarehouseData({ data: null, isLoading: false });
                          setSupplierData({ data: null, isLoading: false });
                          setValue('supplier', null);
                          setValue('warehouse', null);
                        } else {
                          callSupplierApiDomain(optionSelected);
                        }
                      }}
                      valueDefaultKey={optionDefaultCountryDrop?.key}
                    />
                  )}
                  {isAdmin && (
                    <KogAutoCompleteControlled
                      label={getTranslateByKey('information.supplier')}
                      options={supplierData.data}
                      isLoading={supplierData.isLoading}
                      nameCustom="supplier"
                      rulesCustom={{ required: true }}
                      errorMessageCustom={
                        errors.supplier?.type === 'required'
                          ? getTranslateByKey(
                            'information.supplierRequired',
                            true
                          )
                          : ''
                      }
                      setValueCustom={setValue}
                      id="id-modalImportInventory-txt-supplier"
                      control={control}
                      valueDefaultKey={optionDefaultSupplierDrop?.key}
                      onChangeCustom={(optionSelected: KeyValue | null) => {
                        if (optionSelected === null) {
                          setWarehouseData({ data: null, isLoading: false });
                          setValue('warehouse', null);
                        } else {
                          callWarehouseApiDomain(optionSelected);
                        }
                      }}
                      disabled={
                        supplierData.data === null ||
                        optionDefaultSupplierDrop != null
                      }
                    />
                  )}
                  <KogAutoCompleteControlled
                    id="id-modalImportInventory-warehouseName"
                    nameCustom={`warehouse`}
                    placeholder={getTranslateByKey('information.warehouse')}
                    label={getTranslateByKey('information.warehouse')}
                    control={control}
                    setValueCustom={setValue}
                    options={warehouseData.data}
                    isLoading={warehouseData.isLoading}
                    onChangeCustom={() => {
                      if (isNullUndefined(getValues('warehouse')?.key)) {
                      }
                    }}
                    rulesCustom={{ required: true }}
                    errorMessageCustom={
                      errors.warehouse?.type === 'required'
                        ? getTranslateByKey('warehouse.warehouseRequired', true)
                        : ''
                    }
                    valueDefaultKey={optionDefaultWarehouseDrop?.key}
                    disabled={
                      isNullUndefined(getValues('supplier')?.key) ||
                      optionDefaultWarehouseDrop != null
                    }
                  />
                  <KogAutoCompleteControlled
                    id='id-modalImportInventory-select-companyType'
                    nameCustom='companyType'
                    label={getTranslateByKey('information.companyType')}
                    isDefaultValueUnique
                    control={control}
                    setValueCustom={setValue}
                    options={optionsFilteredCompanyType}
                    isLoading={companyTypeReducer.isLoading}
                    rulesCustom={{ required: true }}
                    onChangeCustom={(value) => { onChangeCompanyType(value) }}
                  />
                  {showCompanyControl && (
                    <CompanyAllDropdown idScreen='id-modalImportInventory' />
                  )}

                  <KOGFileUploader
                    files={files}
                    setFiles={setFiles}
                    typeFiles="excel"
                    maxFiles={1}
                  />
                  {uploading && (
                    <S.ProgressContainer>
                      <LinearProgress
                        style={{ flexGrow: 1, marginRight: '8px' }}
                      />
                      <Typography variant="body1">
                        {getTranslateByKey('importInventory.uploading')}...
                      </Typography>
                    </S.ProgressContainer>
                  )}
                  <ButtonSubmit
                    files={files}
                    uploading={uploading}
                  />
                </S.BodyBackground>
                <S.FooterBackground>
                  {isFullVersion && (
                    <S.InfoContainer>
                      <Info />
                      <Typography
                        variant="body1"
                        style={{
                          wordWrap: 'break-word',
                          maxWidth: '90%',
                          color: 'gray',
                          textAlign: 'center'
                        }}>
                        {getTranslateByKey('importInventory.fileHasMoreThanXLines').replace('{0}', parameterMaximumImportLines.toString())}
                      </Typography>
                    </S.InfoContainer>
                  )}
                </S.FooterBackground>
              </form>
            </FormProvider>
          </S.Background>
          <ModalAlert closeByOverlay={false} />
        </KogModalSideComponent>
      )}
    </>
  );
};

export default ModalImportInvetory;
